import { useEffect, useState } from "react";
import { Login } from "./components/Login.js";
import { Shell } from "./components/Shell.js";
import { fetchAuthSession, isAuthConfigured } from "./lib/auth.js";
import { setUnauthorizedHandler } from "./lib/api.js";
import { hashForLogin, parseHash, type Route } from "./lib/hashRoute.js";

export function App() {
  const [ready, setReady] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [route, setRoute] = useState<Route>(() => parseHash());

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setSignedIn(false);
      const current = parseHash();
      const returnTo = current.kind === "app" ? `/${current.view}` : "/dashboard";
      const next = hashForLogin(returnTo);
      if (location.hash !== next) location.hash = next;
    });
  }, []);

  useEffect(() => {
    void (async () => {
      if (!isAuthConfigured()) {
        setReady(true);
        return;
      }
      const session = await fetchAuthSession();
      setSignedIn(Boolean(session.tokens?.idToken));
      setReady(true);
    })();
  }, []);

  useEffect(() => {
    function onHash() {
      setRoute(parseHash());
    }
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    if (!ready) return;
    if (!signedIn && route.kind !== "login") {
      const returnTo = route.kind === "app" ? `/${route.view}` : "/dashboard";
      const next = hashForLogin(returnTo);
      if (location.hash !== next) location.hash = next;
    } else if (signedIn && route.kind === "login") {
      const dest = `#${route.returnTo.startsWith("/") ? route.returnTo : `/${route.returnTo}`}`;
      location.hash = dest.startsWith("#/login") ? "#/dashboard" : dest;
    } else if (signedIn && !location.hash) {
      location.hash = "#/dashboard";
    }
  }, [ready, signedIn, route]);

  if (!ready) {
    return (
      <div className="flex min-h-full items-center justify-center text-sm text-gray-400">
        Loading…
      </div>
    );
  }

  if (!signedIn) {
    return (
      <Login
        onSignedIn={() => setSignedIn(true)}
      />
    );
  }

  const view = route.kind === "app" ? route.view : "dashboard";
  return <Shell view={view} onSignOut={() => setSignedIn(false)} />;
}
